import { useId, useRef, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router';
import { ProxyLabs } from './ProxyLabs';
import { ProxyModels } from './ProxyModels';

const SECTIONS = [
  { value: 'models', label: 'Models' },
  { value: 'labs', label: 'Labs' },
] as const;
type Section = (typeof SECTIONS)[number]['value'];

/** The LLM proxy's models and labs: what `hawk proxy models` and `labs` manage. */
export function ProxyAdminTab() {
  // In the URL, so a reload or a shared link keeps the section.
  const [params, setParams] = useSearchParams();
  const section: Section = params.get('section') === 'labs' ? 'labs' : 'models';
  const select = (next: Section) =>
    setParams(
      p => {
        if (next === 'models') p.delete('section');
        else p.set('section', next);
        return p;
      },
      { replace: true }
    );
  const id = useId();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  // Arrow keys move between tabs, as for any tablist.
  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = (index + step + SECTIONS.length) % SECTIONS.length;
    select(SECTIONS[next].value);
    tabs.current[next]?.focus();
  };

  return (
    <div className="p-4 text-xs text-ink-2">
      <div role="tablist" aria-label="LLM proxy" className="tabs">
        {SECTIONS.map((s, index) => (
          <button
            key={s.value}
            ref={el => {
              tabs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`${id}-${s.value}-tab`}
            aria-selected={section === s.value}
            aria-controls={`${id}-${s.value}`}
            tabIndex={section === s.value ? 0 : -1}
            onClick={() => select(s.value)}
            onKeyDown={event => onKeyDown(event, index)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${id}-${section}`}
        aria-labelledby={`${id}-${section}-tab`}
        className="pt-3"
      >
        {section === 'models' ? <ProxyModels /> : <ProxyLabs />}
      </div>
    </div>
  );
}
